import { makeAutoObservable, flow } from 'mobx';
import AuthService from '@/features/auth/api/AuthService';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_URL } from '@/shared/api/client';
import { env } from '@/shared/config/env';
import type {
  ReferralPayload,
  RegisterPayload,
  ResetPasswordPayload,
  UpdateProfilePayload,
} from '@/features/auth/api/AuthService';

const BESTCHANGE_ID = env.bestChangeId;

export interface UserProfile {
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  wallet_address?: string;
  commission_rate?: number;
  balance?: number;
  tg_id?: string | number;
  [key: string]: unknown;
}

interface AuthUser {
  username?: string;
  [key: string]: unknown;
}

export default class Store {
  user: AuthUser = {};
  userProfile: UserProfile = {};
  isAuth = false;
  isLoading = false;
  erorr = '';
  viewTgBot = true;

  constructor() {
    makeAutoObservable(this);
  }
  setAuth(bool: boolean) {
    this.isAuth = bool;
  }
  setViewTgBot(agentId: string) {
    if (agentId === BESTCHANGE_ID) {
      this.viewTgBot = false;
    }
  }
  setUser(user: AuthUser) {
    this.user = user;
  }

  setUserProfile(profile: UserProfile) {
    this.userProfile = profile;
  }

  setLoading(bool: boolean) {
    this.isLoading = bool;
  }

  setErorr(errMsg: string) {
    this.erorr = errMsg;
  }

  fetchAndUpdateUserProfile = flow(function* (accessToken) {
    const response = yield AuthService.getProfile();
    this.setUserProfile(response.data);
    const decodedToken = jwtDecode(accessToken);
    this.setUser(decodedToken);
    this.setAuth(true);
  });

  login = flow(function* (username, password) {
    try {
      this.setLoading(true);
      const response = yield AuthService.login(username, password);
      localStorage.setItem('access_token', response.data.access);
      Cookies.set('refresh_token', response.data.refresh, {
        expires: 30,
      });
      yield this.fetchAndUpdateUserProfile(response.data.access);
    } finally {
      this.setLoading(false);
    }
  });

  async register(data: RegisterPayload) {
    return await AuthService.register(data);
  }
  async createReferral(data: ReferralPayload) {
    try {
      await AuthService.createReferral(data);
    } catch (error) {}
  }

  async logout() {
    try {
      this.setLoading(true);
      await AuthService.logout();
      localStorage.removeItem('access_token');
      this.setAuth(false);
      this.setUser({});
    } catch (error) {
    } finally {
      this.setLoading(false);
    }
  }

  checkAuth = flow(function* () {
    try {
      this.setLoading(true);
      const refreshToken = Cookies.get('refresh_token');
      const response = yield axios.post(`${API_URL}auth/token/refresh/`, {
        refresh: refreshToken,
      });
      yield this.fetchAndUpdateUserProfile(response.data.access);
    } catch (error) {
      localStorage.removeItem('access_token');
      Cookies.remove('refresh_token');
    } finally {
      this.setLoading(false);
    }
  });

  async updateProfile(data: UpdateProfilePayload) {
    this.setLoading(true);
    try {
      await AuthService.updateProfile(data);
      this.setUserProfile({ ...this.userProfile, ...data });
    } finally {
      this.setLoading(false);
    }
  }

  async resetPassword(data: ResetPasswordPayload) {
    await AuthService.resetPassword(data);
  }
}
